import type { PartyCode } from '../types/election.types';

/**
 * Party names used as strike labels in general elections.
 * In primaries, strike labels are candidate names (e.g. "Josh Shapiro").
 */
const PARTY_STRIKE_NAMES = new Set(['democrat', 'republican', 'green']);

/**
 * Party avatar image paths for major-party strikes.
 * Update these to static CDN URLs once marketing uploads them (MARKETING-9398).
 */
const PARTY_AVATARS: Record<string, { light: string; dark: string }> = {
  DEM: {
    light: '/images/dam/forecasttrader/ui/democratic_party_light.webp',
    dark: '/images/dam/forecasttrader/ui/democratic_party_dark.webp',
  },
  GOP: {
    light: '/images/dam/forecasttrader/ui/republican_party_light.webp',
    dark: '/images/dam/forecasttrader/ui/republican_party_dark.webp',
  },
};

/**
 * Returns true when the candidate/strike name IS a party name (e.g. "Democrat", "Republican").
 * In general elections, strike labels are party names; in primaries they're candidate names.
 * Used to:
 * - Show party logos instead of candidate photos (CandidateAvatar)
 * - Hide redundant party text/badges when the name already IS the party
 */
export function isPartyStrikeName(candidateName: string): boolean {
  const lower = candidateName.toLowerCase();
  return Array.from(PARTY_STRIKE_NAMES).some((party) => lower.includes(party));
}

/**
 * Returns the party avatar URL for a major party, or null if not a major party.
 * Uses light/dark variants based on the current color scheme.
 */
export function getPartyAvatarUrl(party: PartyCode, isDark: boolean): string | null {
  const entry = PARTY_AVATARS[party];
  if (!entry) return null;
  return isDark ? entry.dark : entry.light;
}

/**
 * Build a Ballotpedia search URL from a candidate name.
 * Returns null for party-based strike names (e.g. "Democrat").
 */
export function getBallotpediaUrl(candidateName: string): string | null {
  if (isPartyStrikeName(candidateName)) return null;
  return `https://ballotpedia.org/wiki/index.php?search=${encodeURIComponent(candidateName).replace(/%20/g, '+')}`;
}
